//REACT AND REACT BASED LIBRARIES
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useSWRMutation from "swr/mutation";
//COMPONENTS
import { Box, Container } from "@mui/material";
import AddEditFormCst from "@components/AddEditFormCst";
import AnimationFadeIn from "@components/AnimationFadeIn";
import BackgroundImageCst from "@components/BackgroundImageCst";
import Breadcrumb from "@components/Breadcrumb";
import HeroCst from "@components/HeroCst";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
//CUSTOM HOOKS AND UTILS
import { useFetchDataDetail } from "@hooks/useFetchDetail";
import { ServicesURL } from "@utils/utilsApi";
import { MethodType } from "src/services/type";
import { PagesID } from "@utils/utilsConfigurations";
import { fetcher } from "src/services/config";
import dayjs from "dayjs";
import useSWR from "swr";

const EditPlan: React.FC = () => {
  const { t } = useTranslation(["manage-plans-page"]);
  const { id, IdPlan } = useParams();
  const navigate = useNavigate();
  const [error, showError] = useState(false);

  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
    description?: string;
  }>();

  const { columns, dataDetail, mutate } = useFetchDataDetail(
    `${ServicesURL.getPlan
      .replace(":corporateId", id || "")
      .replace(":planId", IdPlan || "")}`,
    PagesID["corporate.foundedTraining.managePlan.plan.edit"],
  );

  let dataDetailsToForm = {
    ...dataDetail,
    ...{
      fund: {
        value: dataDetail?.fund?.id,
        label: dataDetail?.fund?.fund,
        fund: dataDetail?.fund?.catalog_association_type,
        logo_encoded: dataDetail?.fund?.logo_encoded,
      },
    },
  };

  const { data: fundList } = useSWR(ServicesURL.fund, (url: string) =>
    fetcher(url, MethodType.GET),
  );

  const fundKeyValue = fundList?.map((el: any) => {
    const { logo_encoded } = el;
    const newEl = {
      value: el.id,
      label: el.fund,
      fund: el.catalog_association_type,
      logo_encoded,
    };
    return newEl;
  });

  const { trigger: triggerModifyPlan } = useSWRMutation(
    `${ServicesURL.modifyPlan
      .replace(":corporateId", id || "")
      .replace(":planId", IdPlan || "")}`,
    (url: string, arg: { arg: any }) => {
      return fetcher<any>(`${url}`, MethodType.PUT, { ...arg.arg });
    },
  );

  const inputsException = (col: any, inputForm: any) => {
    if (col.accessorKey === "id-ol") {
      col.disabled = true;
    }
    if (col.accessorKey === "id") {
      col.required = false;
      col.disabled = true;
      return col;
    }
    if (col.accessorKey === "fund") {
      col.required = false;
      col.disabled = true;
      return col;
    }

    const formatStart = (
      dayjs(new Date(inputForm.start_date)).format("YYYY-MM-DD") || ""
    ).replace(/-/g, "");
    const formatEnd = (
      dayjs(new Date(inputForm.end_date)).format("YYYY-MM-DD") || ""
    ).replace(/-/g, "");

    if (
      formatStart &&
      formatEnd &&
      formatStart > formatEnd &&
      (col.accessorKey === "start_date" || col.accessorKey === "end_date")
    ) {
      col.error = "start_end_date";
      showError(true);
    } else if (
      formatStart &&
      formatEnd &&
      formatStart <= formatEnd &&
      (col.accessorKey === "start_date" || col.accessorKey === "end_date")
    ) {
      showError(false);
    }

    return col;
  };

  const onSubmit = async (inputForm: any) => {
    if (!error) {
      const startDate = inputForm.start_date["$d"]
        ? new Date(inputForm.start_date["$d"])
        : inputForm.start_date;
      const endDate = inputForm.end_date["$d"]
        ? new Date(inputForm.end_date["$d"])
        : inputForm.end_date;
      const objToSend = {
        ...inputForm,
        start_date: dayjs(new Date(startDate)).format("YYYY-MM-DD") || "",
        end_date: dayjs(new Date(endDate)).format("YYYY-MM-DD") || "",
        fund: {
          id: inputForm.fund.value,
          fund: inputForm.fund.label,
          catalog_association_type: inputForm.fund.fund,
          logo_encoded: inputForm.fund.logo_encoded,
        },
      };
      const { id, id_ol, ...rest } = objToSend;
      setOpenToast({ s: StatusEnum.LOADING, message: "loading" });

      try {
        await triggerModifyPlan(rest);
        setOpenToast({ s: StatusEnum.OK, message: "" });
      } catch (error) {
        setOpenToast(undefined);
      }
      mutate && mutate(objToSend);
    }
  };

  return (
    <AnimationFadeIn>
      <BackgroundImageCst
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-skills-capabilities.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst title={t("edit.hero-title")} description={""} />
        {columns && (
          <Box
            sx={{
              mt: "25px",
            }}
          >
            <AddEditFormCst
              row={dataDetailsToForm || []}
              columns={columns}
              onSubmit={onSubmit}
              actionsForForms={[]}
              backButton={() => {
                navigate(-1);
              }}
              inputsException={inputsException}
              optionsSelect={fundKeyValue}
            />
          </Box>
        )}
      </Container>
      <ModalConfirmsCst
        open={!!openToast}
        title={""}
        description={""}
        onCallBackClosed={() => {
          setOpenToast(undefined);
        }}
        status={openToast}
      />
    </AnimationFadeIn>
  );
};

export default EditPlan;
